import { Box } from '@material-ui/core';
import { StandardDialog, Button } from '@roc/ui';
import { observer } from 'mobx-react';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect } from 'react';
import { useInterestAllocationStore } from '@roc/feature-payoffs';
import { InterestAllocationTable } from './interestAllocationTable';
import { useDocumentStore } from '@roc/feature-documents';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog } from '@roc/feature-general-tasks';

interface IForeclosureInterestAllocationModalProps {
  loanId: number;
  open: boolean;
  onClose: () => void;
  investor: string;
  document: Document;
}

const useStyles = makeStyles(theme => ({
  entityLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  autoComplete: {
    minWidth: '400px'
  },

  editableInput: {
    backgroundColor: 'transparent',
    border: 'solid lightgrey 1px',
    outline: 'none',
    marginBottom: 0,
    width: '100%',
    borderRadius: 8,
    fontFamily: 'Oswald, sans-serif;',
  },

  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginRight: '8px'
  },
}));

export const ForeclosureInterestAllocationModal = observer(({ loanId, open, onClose, investor, document }: IForeclosureInterestAllocationModalProps) => {
  const classes = useStyles();
  const {
    documentStore: {
      documentFormStore: { interestAllocationStore },
    },
  } = useDocumentStore();
  const taskId = document?.taskId;

  const columns: any[] = [
    {
      name: 'Description',
      width: '10%',
      align: 'left',
      mapping: 'description',
    },
    {
      name: 'Amount',
      width: '18%',
      align: 'center',
      mapping: 'amount',
    },
    {
      name: 'Loan Servicer (LS)',
      width: '18%',
      align: 'center',
      mapping: 'loanServicer',
    },
    {
      name: 'TPO',
      width: '18%',
      align: 'center',
      mapping: 'tpo',
    },
    {
      name: 'Servicer',
      width: '18%',
      align: 'center',
      mapping: 'servicer',
    },
    {
      name: investor || 'Investor',
      width: '18%',
      align: 'center',
      mapping: 'investor',
    },
    {
      name: 'Funder',
      width: '18%',
      align: 'center',
      mapping: 'funder',
    },
    {
      name: 'Loan UPB',
      width: '18%',
      align: 'center',
      mapping: 'loanUPB',
    },
    {
      name: 'Principal Loss',
      width: '18%',
      align: 'center',
      mapping: 'principalLoss',
    },
    {
      name: 'Interest Due',
      width: '18%',
      align: 'center',
      mapping: 'interestDue',
    },
    {
      name: 'Interest Loss',
      width: '18%',
      align: 'center',
      mapping: 'interestLoss',
    },
    {
      name: 'Protective Advance',
      width: '18%',
      align: 'center',
      mapping: 'protectiveAdvance',
    },
    {
      name: 'Protective Advance Loss',
      width: '18%',
      align: 'center',
      mapping: 'protectiveAdvanceLoss',
    },
  ];

  useEffect(() => {
    if (open) {
      interestAllocationStore.getForeclosureInterestAllocation(loanId);
    }
  }, [open])

  const saveAction = async () => {
    await interestAllocationStore.saveForeclosureInterestAllocation(loanId);
    interestAllocationStore.getForeclosureInterestAllocation(loanId);
  }

  const getContent = () => {
    return (
      <Box display="flex" flexDirection="column" height="100%" width="100%">
        <InterestAllocationTable columns={columns} rows={interestAllocationStore?.interestAllocatioDataRows} />
      </Box>
    )
  }
  const dialogActions = (
    <>
      <Box mr={2}>
        <Button
          className={classes.button}
          variant="outlined"
          color="primary"
          onClick={() => {
            onClose();
          }}
          testId="cancel"
        >
          CANCEL
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveAction}
          testId="save"
        >
          SAVE
        </Button>
      </Box>
    </>
  )

  return (
    <>
      {taskId ? (
        <CustomTaskDialog
          open={open}
          title={"Foreclosure Interest Allocation"}
          handleClose={() => {
            onClose();
          }}
          dialogContent={
            getContent()
          }
          dialogActions={dialogActions}
          maxWidth="xl"
          taskId={taskId}
        />

      ) : (
        <StandardDialog
          open={open}
          title={"Foreclosure Interest Allocation"}
          maxWidth="xl"
          handleClose={
            () => {
              onClose();
            }
          }
          dialogContent={
            getContent()
          }
          dialogActions={dialogActions}
        />

      )}
    </>



  );
});